/* @flow */
import { Roles } from 'user/UserConstants';
import BoxBody from 'components/BoxBody';
import { Container } from 'flux/utils';
import CopySubscriberButton from 'groups/components/CopySubscriberButton';
import CurrentUser from 'auth/CurrentUser';
import Fieldset from 'components/Fieldset';
import Group from 'entities/Group';
import Groups from 'collections/Groups';
import GroupActions from 'groups/actions/GroupActions';
import GroupStore from 'groups/stores/GroupStore';
import invariant from 'invariant';
import PropTypes from 'prop-types';
import * as React from 'react';
import Relationship from 'entities/Relationship';
import RelationshipViewGroupList from 'user/components/RelationshipViewGroupList';
import RelationshipViewHeader from 'user/components/RelationshipViewHeader';
import RelationshipViewMediums from 'user/components/RelationshipViewMediums';
import RelationshipViewNotes from 'user/components/RelationshipViewNotes';
import RelationshipViewUntrustedName from 'user/components/RelationshipViewUntrustedName';
import Subscriber from 'entities/Subscriber';
import Subscriptions from 'collections/Subscriptions';
import TeacherDetail from 'user/entities/TeacherDetail';
import TeacherDetailActivitySection from 'user/components/TeacherDetailActivitySection';

import type { User as RelationshipViewHeaderUser } from 'user/components/RelationshipViewHeader';

import cx from 'classnames';

import './styles/RelationshipView.scss';
import 'styles/utilities.scss';

const wrapSubscriptions = subscriptions =>
  subscriptions.map(subscription => {
    const group = GroupStore.getGroup(subscription.getGroupId());
    return { group, subscription };
  });

const wrapGroups = (groups, subscriptions) =>
  groups.map(group => ({
    group,
    subscription: subscriptions.find(sub => group.id === sub.getGroupId()),
  }));

type Props = {
  groupToAdmin?: Group,
  user?: RelationshipViewHeaderUser,
  relationship: Relationship,
  className: any,
  onRequestHide: Function,
};

type State = {
  relationship: Relationship,
  subscriptionsToMe: Subscriptions,
  subscriptionsToUser: Subscriptions,
  subscriber: Subscriber,
  note: any,
  groups: Groups,
  teacherDetail?: TeacherDetail,
};

class RelationshipView extends React.Component<Props, State> {
  static childContextTypes = {
    subscriber: PropTypes.instanceOf(Subscriber).isRequired,
  };

  static getStores() {
    return [GroupStore];
  }

  static getSubscriptionStateFromStores = relationship => {
    const groupStoreLoaded = GroupStore.isLoaded();
    const subscriber = relationship.getRelatedUser();

    return {
      subscriptionsToMe: groupStoreLoaded
        ? relationship.getSubscriptionsToMe()
        : new Subscriptions(),
      subscriptionsToUser: groupStoreLoaded
        ? relationship.getSubscriptionsToUser()
        : new Subscriptions(),
      subscriber,
      note: subscriber.getNote(),
    };
  };

  static calculateState(prevState: ?State, props: Props) {
    const { relationship } = props;

    return Object.assign({}, this.getSubscriptionStateFromStores(relationship), {
      groups: GroupStore.getOwnedGroups(),
    });
  }

  getChildContext() {
    const { relationship } = this.props;
    const subscriber = relationship.getRelatedUser();

    return {
      subscriber,
    };
  }

  componentDidMount() {
    this.fetchTeacherDetailIfAdmin();
    if (!GroupStore.isLoaded()) {
      GroupActions.loadGroups();
    }
  }

  componentWillReceiveProps(newProps) {
    this.setState(RelationshipView.getSubscriptionStateFromStores(newProps.relationship));
  }

  fetchTeacherDetailIfAdmin = () => {
    const orgsWhereUserAdmins = this.props.relationship.getAdminAtOrgIds();
    const currentIsAdmin = orgsWhereUserAdmins.length > 0;

    const relatedUser = this.props.relationship.getRelatedUser();
    const relatedIsTeacher = relatedUser.getRole() === Roles.teacher;

    if (currentIsAdmin && relatedIsTeacher && !this.state.teacherDetail) {
      // any org will work for
      const orgId = orgsWhereUserAdmins[0];

      const onSuccess = teacherDetail => this.setState({ teacherDetail });
      // FIXME @team Graph shouldn't this give user feedback?
      const onError = err => console.warn(err, err && err.stack);

      TeacherDetail.getTeacherByOrgAndUserId(orgId, relatedUser.id).then(onSuccess, onError);
    }
  };

  shouldRenderMediums = () => {
    // If this is a not a subscriber of one of my classes
    // then do not display devices.
    return this.props.relationship.getSubscriptionsToMeIncludingStaffClass().length > 0;
  };

  renderDetails = () => {
    if (this.shouldRenderMediums()) {
      return (
        <div>
          <RelationshipViewMediums subscriber={this.state.subscriber} />
          <RelationshipViewNotes subscriber={this.state.subscriber} />
        </div>
      );
    } else {
      return <span>No details about this person.</span>;
    }
  };

  renderOwnGroups = () => {
    const { user } = this.props;

    const { subscriptionsToMe } = this.state;
    if (subscriptionsToMe.isEmpty()) {
      return null;
    } else {
      return (
        <RelationshipViewGroupList
          legend="Classes joined"
          groupSubscriptions={wrapSubscriptions(subscriptionsToMe)}>
          <footer className="RelationshipView-groups-footer">
            {user && user.is_owner ? (
              <CopySubscriberButton relationship={this.props.relationship} />
            ) : null}
          </footer>
        </RelationshipViewGroupList>
      );
    }
  };

  renderTheirGroups = () => {
    const forAllGroups = () => {
      const { teacherDetail } = this.state;
      invariant(teacherDetail, 'teacherDetail should be set');
      const allTheirGroups = teacherDetail
        .getGroups()
        .map(group => GroupStore.getGroup(group.id) || group)
        .filter(group => !group.isStaffClass());
      return wrapGroups(allTheirGroups, this.state.subscriptionsToUser);
    };

    const forSubscribedGroups = () => {
      // Filter out groups that are owned by the current user from subscribed groups
      const unowned = new Subscriptions(
        this.state.subscriptionsToUser.filter(subscription => {
          const group = GroupStore.getGroup(subscription.getGroupId());
          return group && !group.isOwnedByUser(CurrentUser.getUserId());
        })
      );

      return wrapSubscriptions(unowned);
    };

    const visibles = this.state.teacherDetail ? forAllGroups() : forSubscribedGroups();

    if (visibles.length) {
      return <RelationshipViewGroupList legend="Classes owned" groupSubscriptions={visibles} />;
    } else {
      return null;
    }
  };

  render() {
    return (
      <section className={cx('RelationshipView', this.props.className)}>
        <RelationshipViewHeader
          groupToAdmin={this.props.groupToAdmin}
          relationship={this.props.relationship}
          onRequestHide={this.props.onRequestHide}
          subscriber={this.state.subscriber}
          user={this.props.user}
          teacherDetail={this.state.teacherDetail}
        />
        <RelationshipViewUntrustedName subscriber={this.state.subscriber} />
        <BoxBody>
          {this.state.teacherDetail && (
            <TeacherDetailActivitySection teacherDetail={this.state.teacherDetail} />
          )}
          <Fieldset legend="Details" size="small">
            {this.renderDetails()}
          </Fieldset>
          {this.renderOwnGroups()}
          {this.renderTheirGroups()}
        </BoxBody>
      </section>
    );
  }
}

export default Container.create(RelationshipView, { withProps: true, pure: false });
